import { translate } from '@util/Localization';
import { isDirectToPayRoute } from '@util/RouterUtils';
import { Rate } from 'antd';
import moment from 'moment';
import React, { FunctionComponent, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { capitalise, isDefined, isNonEmptyString } from 'ts-utils';
import { chooseAddonPlanPerExperiment } from '../../../businessLogic/AddonPlans';
import { PROGRAM_LENGTH } from '../../../businessLogic/PlansData';
import { getHcpDetailsFromUrl } from '../../../config/AssessmentUtils';
import { Color } from '../../../config/Color';
import { useAppStore } from '../../../store/Store';
import { PurchaseSelector } from '../../../store/purchase/PurchaseActions';
import * as images from '../../assets/Images';
import {
  bellWhite,
  shieldWhite,
  starOutlineWhite,
  tickWhite,
  unlockWhite,
} from '../../assets/images/Icons';
import { Body, bodyFontStyle } from '../../styles/Typography';
import { PatientAssistanceCta } from '../PatientAssistanceCta';
import { ProductAddon } from '../ProductAddon';
import { HeldCountdown } from './HeldCountdown';
import {
  listForRow,
  planSteps,
  programAnswers,
  ProgramUserData,
  programSchedule,
} from './ProgramAnswers';
import { RedesignCheckoutPanel } from './RedesignCheckoutPanel';
import { ReferralClinician, referralClinician } from './ReferralClinician';

export type PaymentRedesignLayout = 'program' | 'sales';

type PaymentRedesignProps = {
  layout: PaymentRedesignLayout;
  /** The email or Continue step and the card form, shown inside the checkout card. */
  purchaseForm: ReactNode;
  checkoutShown: boolean;
};

/**
 * `PaymentRedesign` experiment arms of the payment page: a Program or Sales story, then the same
 * purchase features as Control (countdown, guarantee, add-on, prices, checkout and PAP).
 */
export const PaymentRedesign: FunctionComponent<PaymentRedesignProps> = (props) => {
  const referralHcpDetails = getHcpDetailsFromUrl();
  const addon = chooseAddonPlanPerExperiment();

  return (
    <Page data-test={`paymentRedesign-${props.layout}`}>
      {isDefined(referralHcpDetails) && (
        <ClinicianCard clinician={referralClinician(referralHcpDetails)} />
      )}
      {props.layout === 'program' ? <ProgramStory /> : <SalesStory />}
      <GuaranteeCard />
      {addon !== undefined && <ProductAddon product={addon} />}
      {/* Sales already leads with its rating, so only Program shows proof before the price. */}
      {props.layout === 'program' && <ReviewsStrip />}
      <RedesignCheckoutPanel checkoutShown={props.checkoutShown}>
        {props.purchaseForm}
      </RedesignCheckoutPanel>
      <PapCta />
    </Page>
  );
};

/** The person's program from their answers, then their plan as a four-step story. */
const ProgramStory: FunctionComponent = () => {
  const name = useAppStore((state) => state.user.name);
  const answers = useAppStore((state) => programAnswers(state.userData as ProgramUserData));
  const schedule = programSchedule(answers);
  const resultsDate = moment().add(WEEKS_TO_RESULTS, 'weeks').format('MMM D');

  return (
    <>
      <ProgramHeading>
        {isNonEmptyString(name)
          ? `${capitalise(name)}, your program is ready`
          : 'Your program is ready'}
      </ProgramHeading>

      <Card>
        {answers.conditions.length > 0 && (
          <DetailRow label="Condition">{listForRow(answers.conditions)}</DetailRow>
        )}
        {answers.subtypes.length > 0 && (
          <DetailRow label="Subtype">{answers.subtypes.join(' · ')}</DetailRow>
        )}
        <DetailRow label="Program">{`${PROGRAM_LENGTH.weeks} weeks · 15 min a day`}</DetailRow>
        {isDefined(schedule) && <DetailRow label="Schedule">{schedule}</DetailRow>}
        {answers.alongside.length > 0 && (
          <DetailRow label="Alongside">{listForRow(answers.alongside)}</DetailRow>
        )}
        <DetailRow label="Typical results">{`From ${resultsDate}`}</DetailRow>
        <Doctor subtitle="Program lead · Monash University" />
      </Card>
      <Countdown />

      <Section>
        <SectionHeading>Your plan</SectionHeading>
        <Timeline
          steps={planSteps(answers, PROGRAM_LENGTH.weeks).map((step, index) => ({
            label: step.title,
            body: step.desc,
            marker: index + 1,
          }))}
        />
      </Section>
    </>
  );
};

// Claim: when people typically start noticing a difference. Copy sign-off owns this number.
const WEEKS_TO_RESULTS = 4;

// App store figures shown in both arms. Claims: keep in step with the store listings.
const STORE_RATING = 4.6;
const REVIEW_COUNT = '30,000+';
const MEMBER_COUNT = '200k+';

const WHAT_YOU_GET: [lead: string, rest: string][] = [
  ['Guided audio sessions', 'you can do at home'],
  ['Tailored to you', 'based on your goals'],
  ['Symptom tracking', 'to see how things change'],
  ['Flare-up sessions', 'for after the program'],
  ['Real human support', 'via in-app chat'],
];

/** Leads with the offer, then what's included, social proof and how the free trial works. */
const SalesStory: FunctionComponent = () => {
  const { hasFreeTrial, trialPeriodDays } = useAppStore((state) => ({
    hasFreeTrial: PurchaseSelector.hasFreeTrial(state.purchase),
    trialPeriodDays: state.purchase.trialPeriodDays,
  }));
  // We remind people two days before their trial ends.
  const reminderDate = moment()
    .add(trialPeriodDays - 2, 'days')
    .format('MMM D');
  const membershipDate = moment().add(trialPeriodDays, 'days').format('MMM D');

  return (
    <>
      <Hero>
        <SalesHeading>Start feeling better.</SalesHeading>
        <Lead>An evidence-based gut-brain program you do at home, 15 minutes a day.</Lead>
      </Hero>

      <ProofRow>
        <Doctor subtitle="Monash University" />
        <Rating>
          <Stars rating={STORE_RATING} label={`Rated ${STORE_RATING} out of 5`} />
          <Muted>{`${STORE_RATING} · ${REVIEW_COUNT} ratings`}</Muted>
        </Rating>
      </ProofRow>
      <Countdown />

      <Section>
        <SectionHeading>What you get</SectionHeading>
        <Benefits>
          {WHAT_YOU_GET.map(([lead, rest]) => (
            <Benefit key={lead}>
              <TickBadge>
                <img src={tickWhite} alt="" width={16} height={16} />
              </TickBadge>
              <BenefitText>
                <strong>{lead}</strong> {rest}
              </BenefitText>
            </Benefit>
          ))}
        </Benefits>
      </Section>

      <Quote>
        <QuoteText>“I can eat all the foods and enjoy all the foods that I used to.”</QuoteText>
        <Muted>Marianna, Nerva member</Muted>
      </Quote>

      {hasFreeTrial && trialPeriodDays > 2 && (
        <Section>
          <SectionHeading>How your free trial works</SectionHeading>
          <Timeline
            steps={[
              {
                label: 'Today',
                body: 'Full access to your program.',
                marker: <img src={unlockWhite} alt="" width={15} height={15} />,
                filled: true,
              },
              {
                label: reminderDate,
                body: 'We remind you before your trial ends.',
                marker: <img src={bellWhite} alt="" width={15} height={15} />,
              },
              {
                label: membershipDate,
                body: 'Your membership begins.',
                marker: <img src={starOutlineWhite} alt="" width={15} height={15} />,
              },
            ]}
          />
        </Section>
      )}
    </>
  );
};

/** Control's countdown sends people back to their results at zero, so /start doesn't show it. */
const Countdown: FunctionComponent = () => (isDirectToPayRoute() ? null : <HeldCountdown />);

/** The clinician who referred them, from the referral link. We have no photo, so initials stand in. */
const ClinicianCard: FunctionComponent<{ clinician: ReferralClinician }> = (props) => (
  <ReferralCard data-test="referralClinician">
    <Initials aria-hidden>{props.clinician.initials}</Initials>
    <div>
      <ReferralLabel>Referred by your clinician</ReferralLabel>
      <DoctorName>{props.clinician.name}</DoctorName>
      <Muted>{props.clinician.clinic}</Muted>
    </div>
  </ReferralCard>
);

/** Members and reviews, shown above the checkout card in the Program arm. */
const ReviewsStrip: FunctionComponent = () => (
  <Strip>
    <Stars
      rating={STORE_RATING}
      label={`Rated ${STORE_RATING} out of 5 on the Apple App Store and Google Play`}
    />
    <Stats>
      <Stat>
        <StatValue>{MEMBER_COUNT}</StatValue>
        <StatLabel>members</StatLabel>
      </Stat>
      <StatDivider />
      <Stat>
        <StatValue>{REVIEW_COUNT}</StatValue>
        <StatLabel>reviews</StatLabel>
      </Stat>
    </Stats>
    <StatLabel>On the Apple App Store and Google Play</StatLabel>
  </Strip>
);

const GuaranteeCard: FunctionComponent = () => (
  <IconCard data-test="moneyBackGuarantee">
    <IconBadge>
      <img src={shieldWhite} alt="" width={18} height={18} />
    </IconBadge>
    <div>
      <CardTitle>Money back guarantee</CardTitle>
      {/* Same terms as Control, so a policy change reaches every arm. */}
      <CardBody>{translate('payment.guarantee.aiPersonalizedInfo.info')}</CardBody>
    </div>
  </IconCard>
);

/** The store rating in antd's stars, to the nearest half star. */
const Stars: FunctionComponent<{ rating: number; label: string }> = (props) => (
  <span role="img" aria-label={props.label}>
    <StoreRate disabled allowHalf value={Math.round(props.rating * 2) / 2} />
  </span>
);

const DetailRow: FunctionComponent<{ label: string; children: ReactNode }> = (props) => (
  <Row>
    <RowLabel>{props.label}</RowLabel>
    <RowValue>{props.children}</RowValue>
  </Row>
);

const Doctor: FunctionComponent<{ subtitle: string }> = (props) => (
  <DoctorRow>
    <Avatar src={images.simonePeters2024} alt="" width={44} height={44} />
    <div>
      <DoctorName>{translate('doctorShort')}</DoctorName>
      <Muted>{props.subtitle}</Muted>
    </div>
  </DoctorRow>
);

type TimelineStep = {
  label: string;
  body: string;
  /** A step number or an icon. */
  marker: ReactNode;
  filled?: boolean;
};

const Timeline: FunctionComponent<{ steps: TimelineStep[] }> = (props) => (
  <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
    {props.steps.map((step, index) => {
      const last = index === props.steps.length - 1;
      return (
        <Step key={step.label}>
          <Rail>
            <Marker filled={step.filled === true}>{step.marker}</Marker>
            {!last && <RailLine />}
          </Rail>
          <StepText last={last}>
            <StepLabel>{step.label}</StepLabel>
            <StepBody>{step.body}</StepBody>
          </StepText>
        </Step>
      );
    })}
  </ol>
);

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  /* Bottom room so the checkout card never meets the screen edge when it's the last element. */
  padding: ${({ theme }) => `${theme.spacing._4px} ${theme.spacing._20px} ${theme.spacing._48px}`};
  text-align: left;
`;

const Text = styled(Body)`
  margin: 0;
`;

const Muted = styled(Text)`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.light};
  color: ${Color.content.muted};
`;

// Real headings (h1 for the page, h2 per section) so screen readers can navigate the page.
const headingStyle = css`
  ${bodyFontStyle}
  margin: 0;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  letter-spacing: -0.019em;
`;

const ProgramHeading = styled.h1`
  ${headingStyle}
  font-size: 32px;
  line-height: 1.12;
`;

const SalesHeading = styled.h1`
  ${headingStyle}
  font-size: 40px;
  line-height: 1.05;
`;

const SectionHeading = styled.h2`
  ${headingStyle}
  font-size: 22px;
  line-height: 1.15;
`;

const Hero = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing._12px};
`;

const Lead = styled(Text)`
  font-size: 17px;
  font-weight: ${({ theme }) => theme.fontWeight.light};
  line-height: 1.45;
  color: ${Color.content.muted};
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => `${theme.spacing._4px} ${theme.spacing._20px} ${theme.spacing._20px}`};
  border-radius: 20px;
  border: 0.6px solid ${Color.decorative.default};
  background: ${Color.surface.onAccent};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._12px};
  padding: ${({ theme }) => `${theme.spacing._14px} 0`};
  border-bottom: 0.6px solid ${Color.decorative.default};
`;

const RowLabel = styled(Text)`
  /* Long values wrap on the right, so the label stays on one line. */
  flex-shrink: 0;
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeight.light};
  color: ${Color.content.muted};
`;

const RowValue = styled.div`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  text-align: right;
  color: ${Color.content.default};
`;

const DoctorRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._14px};
  padding-top: ${({ theme }) => theme.spacing._18px};
`;

const Avatar = styled.img`
  border-radius: 50%;
  flex-shrink: 0;
`;

const DoctorName = styled(Text)`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const ProofRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._12px};
  padding-bottom: ${({ theme }) => theme.spacing._16px};
  border-top: 0.6px solid ${Color.decorative.default};
  border-bottom: 0.6px solid ${Color.decorative.default};

  ${DoctorRow} {
    padding-top: ${({ theme }) => theme.spacing._16px};
  }
`;

const Rating = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  /* Below this width it wraps under the doctor, so align it with them. */
  @media (max-width: 359px) {
    align-items: flex-start;
  }
  gap: ${({ theme }) => theme.spacing._4px};
  padding-top: ${({ theme }) => theme.spacing._16px};
`;

const Benefits = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing._14px};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Benefit = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing._14px};
`;

const TickBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${Color.surface.onAccent};
`;

const BenefitText = styled(Text)`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeight.light};
  line-height: 1.45;
  color: ${Color.content.muted};

  strong {
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    color: ${Color.content.default};
  }
`;

const Quote = styled.figure`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing._12px};
  margin: 0;
  padding: ${({ theme }) => `22px ${theme.spacing._20px}`};
  border-radius: 20px;
  border: 0.6px solid ${Color.decorative.default};
  background: ${Color.surface.onAccent};
`;

const QuoteText = styled(Text)`
  font-size: 19px;
  line-height: 1.4;
  letter-spacing: -0.01em;
`;

const ReferralCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._14px};
  /* Sits 16px above the headline, as one group with it. */
  margin-bottom: -12px;
  padding: ${({ theme }) => theme.spacing._16px};
  border-radius: 20px;
  border: 0.6px solid ${Color.content.onAccent};
  background: ${Color.surface.onAccent};
`;

const Initials = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${Color.action.default};
  color: ${Color.content.default};
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  letter-spacing: 0.02em;
`;

const ReferralLabel = styled(Text)`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${Color.content.onAccent};
`;

const IconCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing._14px};
  padding: ${({ theme }) => theme.spacing._20px};
  border-radius: 20px;
  border: 0.6px solid ${Color.decorative.default};
  background: ${Color.surface.onAccent};
`;

const IconBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${Color.surface.onAccent};
`;

const CardTitle = styled(Text)`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const CardBody = styled(Text)`
  margin-top: ${({ theme }) => theme.spacing._4px};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.light};
  line-height: 1.45;
  color: ${Color.content.muted};
`;

const Step = styled.li`
  display: flex;
  gap: ${({ theme }) => theme.spacing._16px};
`;

const Rail = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 30px;
`;

const Marker = styled.span<{ filled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: ${({ filled }) => (filled ? 'none' : `1px solid ${Color.flow.disabled}`)};
  background: ${({ filled }) => (filled ? Color.action.default : 'transparent')};
  color: ${Color.content.default};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const RailLine = styled.span`
  flex: 1;
  width: 1px;
  margin: ${({ theme }) => `${theme.spacing._6px} 0`};
  background: ${Color.decorative.default};
`;

const StepText = styled.div<{ last: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing._4px};
  padding: ${({ last }) => (last ? '5px 0 0' : '5px 0 26px')};
`;

const StepLabel = styled(Text)`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${Color.content.onAccent};
`;

const StepBody = styled(Text)`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeight.light};
  line-height: 1.45;
`;

const PapCta = styled(PatientAssistanceCta)`
  margin: 0;
  padding: 0;
  border: none;

  p {
    margin-bottom: ${({ theme }) => theme.spacing._6px};
    font-size: 15px;
    font-weight: ${({ theme }) => theme.fontWeight.light};
    color: ${Color.content.muted};
  }

  button {
    font-size: 15px;
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    color: ${Color.content.default};
    text-underline-offset: 3px;
  }
`;

const Strip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._12px};
`;

const Stats = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._24px};
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const StatValue = styled(Body)`
  margin: 0;
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  letter-spacing: -0.019em;
`;

const StatLabel = styled(Body)`
  margin: 0;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.light};
  color: ${Color.content.muted};
`;

const StoreRate = styled(Rate)`
  display: flex;
  gap: 2px;
  font-size: 14px;
  color: ${Color.star};

  .ant-rate-star {
    margin: 0;
  }
  .ant-rate-star-zero .ant-rate-star-first,
  .ant-rate-star-half .ant-rate-star-second,
  .ant-rate-star-zero .ant-rate-star-second {
    color: ${Color.decorative.default};
  }
`;

const StatDivider = styled.div`
  width: 0.6px;
  height: 36px;
  background: ${Color.decorative.default};
`;
